import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'antd';
import { TableFullRowSelect } from '@spidertracks/components';
import {
  deleteCustomPois,
  populateCustomPois,
  populateLocationTypes
} from '../../../redux/slice/poiAdmin/thunk';
import {
  getCustomPois,
  getCustomPoisPageNumber,
  getCustomPoisTotal,
  getLocationTypes,
  getLocationTypesLoading,
  getPoisLoading
} from '../../../redux/selectors/poiAdmin';
import { getUserData } from '../../../redux/selectors/userData';
import {
  dateFormatter,
  formatStandardDateStrings,
  fromTimestamp
} from '../../../helpers/formatTime';
import {
  setCustomPoisPageNumber,
  setSelectedCustomPoi,
  setSelectedCustomPoiIds
} from '../../../redux/slice/poiAdmin/poiAdmin';
import { getLocationTypeDisplayName } from '../../../util/poi';
import { showCustomPoiDeleteModal } from './CustomPoiDeleteModal';

interface ComponentProps {
  organisationId: string;
}

interface TableRow {
  key: React.Key;
  code: string;
  name: string;
  type: string;
  created: string;
}

export const PAGE_SIZE = 30;

const CustomPoiContainer = ({ organisationId }: ComponentProps) => {
  const dispatch = useDispatch();
  const customPois = useSelector(getCustomPois);
  const customPoisTotal = useSelector(getCustomPoisTotal);
  const customPoisPageNumber = useSelector(getCustomPoisPageNumber);
  const locationTypes = useSelector(getLocationTypes);
  const poisLoading = useSelector(getPoisLoading);
  const locationTypesLoading = useSelector(getLocationTypesLoading);
  const userData = useSelector(getUserData);

  const [tableRows, setTableRows] = useState<TableRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const columns = [
    {
      title: 'CODE',
      dataIndex: 'code',
      key: 'code',
      defaultSortOrder: 'ascend',
      sorter: (a: TableRow, b: TableRow) =>
        a.code.toLowerCase().localeCompare(b.code.toLowerCase(), undefined, { sensitivity: 'base' })
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      defaultSortOrder: 'ascend',
      sorter: (a: TableRow, b: TableRow) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase(), undefined, { sensitivity: 'base' })
    },
    {
      title: 'TYPE',
      dataIndex: 'type',
      key: 'type'
    },
    {
      title: 'CREATED',
      dataIndex: 'created',
      key: 'created'
    }
  ];

  useEffect(() => {
    dispatch(populateLocationTypes());
  }, [organisationId, dispatch]);

  useEffect(() => {
    dispatch(populateCustomPois(organisationId));
  }, [customPoisPageNumber, dispatch, organisationId]);

  useEffect(() => {
    if (locationTypes.length === 0 || !userData || !userData.dateFormat || !userData.timezone) {
      return;
    }

    const getFormattedCreatedTime = (created: number) => {
      const dateFormat = userData.dateFormat.replace(/_/g, '/') + ' z';
      const formatFn = dateFormatter(dateFormat);
      const localMoment = fromTimestamp(created);
      const [localTime] = formatStandardDateStrings(formatFn, localMoment, userData.timezone);
      return localTime;
    };

    const tableRows = customPois.map(poi => {
      const locationType = locationTypes.find(type => type.id === poi.locationTypeId);
      const locationTypeDisplayName = locationType
        ? getLocationTypeDisplayName(locationType)
        : 'unknown';

      return {
        key: poi.locationId.toString(),
        code: poi.code,
        name: poi.name,
        type: locationTypeDisplayName,
        created: getFormattedCreatedTime(poi.createdAt)
      };
    });

    setTableRows(tableRows);
  }, [customPois, locationTypes, userData]);

  const onChange = (selectedRowKeys: string[]) => {
    setSelectedRowKeys(selectedRowKeys);
    dispatch(setSelectedCustomPoiIds(selectedRowKeys));
  };

  const handleDelete = () => {
    showCustomPoiDeleteModal({
      selectedRowKeys,
      onDelete: () => {
        dispatch(deleteCustomPois(organisationId));
      }
    });
  };

  const handleOnRow = (row: TableRow) => ({
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      const key = row.key.toString();
      const selectedCustomPoi = customPois.find(poi => poi.locationId.toString() === key);
      if (selectedCustomPoi) {
        dispatch(setSelectedCustomPoiIds([selectedCustomPoi.locationId]));
        dispatch(setSelectedCustomPoi(selectedCustomPoi));
      }
    }
  });

  const handleTableChange = (pagination: { current: number }) => {
    dispatch(setCustomPoisPageNumber(pagination.current));
  };

  return (
    <div className="px-5 py-3" style={{ height: '100%', overflow: 'auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <h4>Custom Locations</h4>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'flex-end'
        }}
      >
        <Button
          data-testid={'delete-poi'}
          type="link"
          icon="delete"
          size="small"
          onClick={handleDelete}
          disabled={selectedRowKeys.length === 0}
        >
          Remove
        </Button>
      </div>
      <div style={{ marginBottom: '10px', overflow: 'auto' }}>
        <TableFullRowSelect
          data-testid={'poi-table'}
          pagination={{
            current: customPoisPageNumber,
            total: customPoisTotal,
            pageSize: PAGE_SIZE,
            hideOnSinglePage: true
          }}
          rowKey="key"
          size="middle"
          bordered
          dataSource={tableRows}
          columns={columns}
          loading={poisLoading || locationTypesLoading}
          onRow={handleOnRow}
          rowSelection={{
            selectedRowKeys,
            onChange
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default CustomPoiContainer;
